import {Config} from '../../ts/services/config';
import {Module, PubSub} from '@mamdev/cad.static-next-lib';
import Viewport from '../../ts/services/viewport';
import Features from '../../ts/services/features';
import Scrolling from '../../ts/services/scrolling';
import Tracking2 from '../../ts/services/tracking2';

/**
 * Manages the content of pages like article or standalone
 */
export default class Content extends Module {
  private wasFeedbackDialogSuccessful = false;
  private articleStartCord = 0;
  private feedbackDialog: HTMLDialogElement | undefined;
  private feedbackButtons: HTMLCollectionOf<HTMLElement> | undefined;
  private feedbackDiv: HTMLElement | undefined;
  private feedbackDialogClose: HTMLElement | undefined;
  private headlineElement: HTMLElement | undefined;
  private articleEnd: HTMLElement;
  private articleScrollProgressContainer: HTMLDivElement | null;
  private articleScrollProgressBar: HTMLProgressElement | null;
  private articleScrollProgressLabel: HTMLDivElement | null;
  private article: HTMLElement | undefined;
  private contentTotalSlidesElement: HTMLElement | undefined;
  private taboola: HTMLElement | undefined;
  private coremId: number;

  static attachTo(root: HTMLElement): Content {
    return new Content(root);
  }

  constructor(root: HTMLElement) {
    super(root);
    this.name_ = 'content';
  }

  /**
   * Starts the module
   */
  start() {
    this.headlineElement = document.getElementsByClassName('content__headline')[0] as HTMLElement;
    this.feedbackDiv = this.root_.getElementsByClassName('content__feedback')[0] as HTMLElement;
    this.feedbackButtons = this.root_.getElementsByClassName('content__feedback-link') as HTMLCollectionOf<HTMLElement>;
    this.feedbackDialog = this.root_.getElementsByClassName('feedback__dialog')[0] as HTMLDialogElement;
    this.feedbackDialogClose = document.getElementsByClassName('feedback__dialog__close')[0] as HTMLElement;
    this.articleEnd = this.root_.getElementsByClassName('content__meta')[1] as HTMLElement;
    this.articleScrollProgressContainer = this.root_.querySelector('.scroll-progress-container');
    this.articleScrollProgressBar = this.root_.querySelector('.scroll-progressbar');
    this.articleScrollProgressLabel = this.root_.querySelector('.scroll-progress-label');
    this.article = this.root_.getElementsByClassName('article-body')[0] as HTMLElement;
    this.contentTotalSlidesElement = this.root_.getElementsByClassName('content__total-slides')[0] as HTMLElement;
    this.taboola = this.root_.getElementsByClassName('co-relatedbox')[0] as HTMLElement;
    const trackedLinks = this.root_.querySelectorAll('a[data-component-path]:not(.teaser a):not([data-mod-name="intro"] a):not([data-mod-name="native-image"] a)');
    this.coremId = (Config.get('pageinfo') as {coremid: number}).coremid;

    if (this.article) this.articleStartCord = Viewport.getElementYOffset(this.article);

    Scrolling.recalcScrollEnd(this.articleEnd, this.articleStartCord);

    Tracking2.bindTrackingExposeHandler(this.articleEnd);

    Tracking2.bindTrackingClickHandler(trackedLinks);

    if (Features.isDialogElementSupported()) {
      if (this.feedbackDiv) {
        this.feedbackDiv.classList.remove('collapsed');
      }

      if (this.feedbackButtons.length) {
        Array.from(this.feedbackButtons).forEach((feedbackButton: HTMLElement) => {
          feedbackButton.addEventListener('click', e => {
            e.preventDefault();
            this.showFeedbackDialog();
          });
        });
      }

      if (this.feedbackDialog) {
        this.feedbackDialog.addEventListener('cancel', e => {
          e.preventDefault();
          this.hideFeedbackDialog();
        });
        this.feedbackDialog.addEventListener('close', e => {
          e.preventDefault();
          this.afterClosingFeedbackDialog();
        });
      }

      if (this.feedbackDialogClose) {
        this.feedbackDialogClose.addEventListener('click', e => {
          e.preventDefault();
          this.hideFeedbackDialog();
        });
      }

      if (this.contentTotalSlidesElement) {
        this.contentTotalSlidesElement.addEventListener('click', e => {
          e.preventDefault();
          this.publish('content:scrollToGallery');
        });
      }

      this.subscribe('content:successfulFeedbackDialog', () => {
        this.wasFeedbackDialogSuccessful = true;
      });

      this.subscribe('content:closeFeedbackDialog', () => {
        this.hideFeedbackDialog();
      });
    }

    // Enable animations on content elements
    this.root_.classList.add('content-animated');

    this.subscribe('imagecaption:open', () => {
      if (Viewport.isTablet()) {
        const descriptionElement = this.root_.querySelector('.article__featured-image .description');
        if (!descriptionElement || !this.headlineElement) return;
        const heightSubTextOpen = descriptionElement.getBoundingClientRect().height;
        this.headlineElement.style.paddingBottom = (heightSubTextOpen + 40) + 'px';
      }
    });

    this.subscribe('imagecaption:close', () => {
      if (Viewport.isTablet() && this.headlineElement) {
        // Let the element use the padding set via CSS
        this.headlineElement.style.paddingBottom = '';
      }
    });

    // if the page is clicked anywhere, show the 4th tab
    this.root_.addEventListener('click', () => {
      if (Viewport.isDesktop()) {
        this.publish('article:bodyClicked');
      }
    });

    if (this.taboola) {
      Scrolling.observeElement(function() {
        PubSub.publish('content:articleReadProgress', {
          click1: 'undefined',
          click2: 'undefined',
          click3: 'undefined',
          click4: 'undefined',
          clickname: 'scroll_taboola'
        });
        // CADNPCA-17564 also send pixel to T2
        Tracking2.callTealiumLink({
          businessEventType: 'expose',
          componentName: 'taboolaView_0',
          componentPath: 'content.article.taboola'
        });
      }).observe(this.taboola);
    }

    this.subscribe('windowScrolling', () => {
      Scrolling.handleScrollProgress(this.articleStartCord, this.articleEnd);
    });

    this.subscribe('window:resize', () => {
      Scrolling.recalcScrollEnd(this.articleEnd, this.articleStartCord);
    });

    this.subscribe('layoutChanged', () => {
      Scrolling.recalcScrollEnd(this.articleEnd, this.articleStartCord);
    });

    this.subscribe('updateProgressBar', scrollPercent => {
      const currentProgress = Math.min(scrollPercent as number, 100);
      const isComplete = currentProgress >= 100;

      if (this.articleScrollProgressBar) {
        this.articleScrollProgressBar.value = currentProgress;
      }

      if (this.articleScrollProgressContainer) {
        this.articleScrollProgressContainer.classList.toggle('scroll-complete', isComplete);
      }

      if (this.articleScrollProgressLabel) {
        this.articleScrollProgressLabel.textContent = `${currentProgress}% gelesen`;
      }
    });
  }

  /**
   * Start bounceOut animation and close the feedback dialog
   */
  hideFeedbackDialog() {
    if (this.wasFeedbackDialogSuccessful) {
      this.feedbackDialog?.classList.add('closeSuccess');
    } else {
      this.feedbackDialog?.classList.add('close');
    }

    const dialog = this.feedbackDialog;
    this.feedbackDialog?.addEventListener('animationend', function animationEndHandler() {
      dialog?.classList.remove('closeSuccess');
      dialog?.classList.remove('close');
      dialog?.close();
      dialog?.removeEventListener('animationend', animationEndHandler, false);
      Viewport.showScrollbar();
    }, false);
  }

  /**
   * Shows the feedback dialog form
   */
  showFeedbackDialog() {
    try {
      Viewport.hideScrollbar();
      this.wasFeedbackDialogSuccessful = false;
      this.feedbackDialog?.showModal();
      Tracking2.callTealiumView({newContentName: 'feedback-layer', newPageType: 'layer'});
      this.publish('feedback:form', {
        referrer: '&referrer=undefined',
        click1: '&click1=' + this.coremId,
        click2: '&click2=feedbackmail',
        click3: '&click3=open',
        click4: '&click4=1',
        clickname: '&clickname=feedbackmail_open'
      });
      this.publish('video:pauseAllPlayers');
      this.publish('welcomeback:stopWBTimer');
      this.publish('globalReco:userFeedback', 'feedbackCount');
    } catch (e) {
      this.logUnknownError(e, false, true);
    }
  }

  /**
   * Called after the feedback dialog is closed
   */
  afterClosingFeedbackDialog() {
    let clickname = '';
    let modulename = '';
    let click3 = '';
    let componentPath = '';

    if (this.wasFeedbackDialogSuccessful) {
      clickname = 'feedbackmail_success_close';
      modulename = 'feedback_success';
      click3 = 'success_close';
      componentPath = 'feedback_dialog.form-group.success_close';
    } else {
      clickname = 'feedbackmail_close';
      modulename = 'feedbackmail';
      click3 = 'close';
      componentPath = 'feedback_dialog.form-group.close';
    }

    if (Config.get('brain')) {
      // set clickname 1-4 piParams for tracking
      window.ui.brain.trigger
        = '.' + this.coremId
          + '.' + modulename
          + '.' + 'close'
          + '.1';
    }

    this.publish('welcomeback:restartWBTimer');
    this.publish('feedback:form', {
      referrer: '&referrer=undefined',
      click1: '&click1=' + this.coremId,
      click2: '&click2=feedbackmail',
      click3: '&click3=' + click3,
      click4: '&click4=1',
      clickname: '&clickname=' + clickname
    });
    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentPath,
      elementTagName: 'div',
      eventType: 'mousedown'
    });
  }
}
